// packages/web/src/api/email-templates/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  emailTemplatesUpsert,
  emailTemplatesDelete,
  emailTemplatesPreview,
  emailTemplatesSendTest,
} from "../generated/sdk.gen.ts";
import type {
  EmailTemplatesUpsertData,
  EmailTemplatesPreviewData,
  EmailTemplatesSendTestData,
} from "../generated/types.gen.ts";

type UpsertBody = NonNullable<EmailTemplatesUpsertData["body"]>;
type PreviewBody = NonNullable<EmailTemplatesPreviewData["body"]>;
type SendTestBody = NonNullable<EmailTemplatesSendTestData["body"]>;

export function useUpsertEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { key: string; locale: string; body: UpsertBody }) => {
      const { data, error } = await emailTemplatesUpsert({
        path: { key: vars.key, locale: vars.locale },
        body: vars.body,
      });
      if (error) throw error;
      if (!data) throw new Error("emailTemplatesUpsert returned no data");
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["email-templates", "list"] });
      void qc.invalidateQueries({ queryKey: ["email-templates", "detail", vars.key] });
    },
  });
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { key: string; locale: string }) => {
      const { data, error } = await emailTemplatesDelete({
        path: { key: vars.key, locale: vars.locale },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["email-templates", "list"] });
      void qc.invalidateQueries({ queryKey: ["email-templates", "detail", vars.key] });
    },
  });
}

export function usePreviewEmailTemplate() {
  return useMutation({
    mutationFn: async (vars: { key: string; body: PreviewBody }) => {
      const { data, error } = await emailTemplatesPreview({
        path: { key: vars.key },
        body: vars.body,
      });
      if (error) throw error;
      if (!data) throw new Error("emailTemplatesPreview returned no data");
      return data;
    },
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (vars: { key: string; body: SendTestBody }) => {
      const { data, error } = await emailTemplatesSendTest({
        path: { key: vars.key },
        body: vars.body,
      });
      if (error) throw error;
      if (!data) throw new Error("emailTemplatesSendTest returned no data");
      return data;
    },
  });
}
